import { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Accordion } from 'jl-design-system/components/accordion/Accordion';
import { AccordionItem } from 'jl-design-system/components/accordion/accordion-item/AccordionItem';
import Tooltip from 'jl-design-system/components/tooltip/Tooltip';

import { Button } from '~/elements';
import { analyticsListeners } from '~/analytics';
import { Media, selectableItem } from '~/components';
import { msToAmPmHours } from '~/routes/utils';

import { TimeSlot } from '../time-slot';

import styles from './TimeSlotsSectionWithAccordion.scss';

const { triggerSelectTimeSlotAnalytics } = analyticsListeners;

const SelectableTimeSlot = selectableItem(TimeSlot);

export class TimeSlotsSectionWithAccordion extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayedSectionName: props.shouldBeClosed ? '' : props.displayedSectionName,
      selectedDate: props.selectedDate,
      isCollapsed: props.shouldCollapse(
        !props.isAppointmentBeingEdited &&
          props.selectedTimeSlot &&
          props.selectedTimeSlot.from > 0,
      ),
      componentRendered: false,
    };
  }

  componentDidMount() {
    const { isAppointmentBeingEdited } = this.props;

    if (isAppointmentBeingEdited) {
      this.selectTimeSlot(
        {
          from: 0,
          eventId: '',
        },
        false,
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.componentRendered(prevProps, prevState);
    this.updateSelectedSection(prevProps, prevState);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.selectedDate !== state.selectedDate) {
      return {
        displayedSectionName: props.shouldBeClosed ? '' : props.displayedSectionName,
        selectedDate: props.selectedDate,
        isCollapsed: props.shouldCollapse(false),
        componentRendered: false,
      };
    }

    return null;
  }

  componentRendered = (prevProps) => {
    const { selectedDate } = this.props;
    const { componentRendered } = this.state;
    if (!componentRendered && prevProps.selectedDate === selectedDate) {
      this.setState({ componentRendered: true });
    }
  };

  updateSelectedSection = (prevProps, prevState) => {
    const { displayedSectionName, hasSelectedRelatedTimeSlot } = this.props;
    const { isCollapsed, displayedSectionName: currentStateDisplayedSectionName } = this.state;
    if (
      prevProps.displayedSectionName !== displayedSectionName ||
      (hasSelectedRelatedTimeSlot &&
        prevState.displayedSectionName !== displayedSectionName &&
        prevState.displayedSectionName === currentStateDisplayedSectionName)
    ) {
      if (prevState.isCollapsed || isCollapsed) {
        this.setState({ displayedSectionName: prevProps.displayedSectionName });
        return;
      }
      this.setState({ displayedSectionName });
    }
  };

  selectTimeSlot = (selectedTimeSlot, collapse = true) => {
    const { appointmentType, selectTimeSlot: dispatchSelectTimeSlot, shouldCollapse } = this.props;

    dispatchSelectTimeSlot({
      ...selectedTimeSlot,
      appointmentTypeId: appointmentType.id,
    });
    this.setState({
      isCollapsed: shouldCollapse(collapse),
    });
    triggerSelectTimeSlotAnalytics();
  };

  onAccordionChangeHandler = (event, sectionName) => {
    // SUMMARY is a tag which is used in the Accordion component
    if (event.target.tagName === 'SUMMARY') {
      event.preventDefault();
      const { componentRendered } = this.state;
      this.setState((prevState) => ({
        displayedSectionName:
          prevState.displayedSectionName === sectionName && componentRendered ? '' : sectionName,
      }));
    }
  };

  changeTimeSlot = () => {
    this.selectTimeSlot(
      {
        from: 0,
        eventId: '',
      },
      false,
    );
  };

  render() {
    const { displayedSectionName, isCollapsed, componentRendered } = this.state;
    const {
      slots,
      appointmentType: { name: appointmentTypeName, id: appointmentTypeId },
      selectedTimeSlot,
      timeSlotAccordionTitle,
      timeSlotTooltipText,
    } = this.props;

    return (
      <Media>
        {({ screenWidth }) => (
          <div
            className={classnames(styles.TimeSlotsSectionWithAccordion, {
              [styles.TimeSlotsSectionWithAccordion_is_selected]: isCollapsed,
            })}
          >
            <div className={styles.TimeSlotsSectionWithAccordion__container}>
              <div className={styles.TimeSlotsSectionWithAccordion__heading}>
                <h3 className={styles.TimeSlotsSectionWithAccordion__title}>
                  {timeSlotAccordionTitle}
                </h3>
                <p>{appointmentTypeName}</p>
              </div>

              {isCollapsed ? (
                <>
                  <SelectableTimeSlot
                    propName="slot"
                    slot={selectedTimeSlot}
                    isChecked
                    isDisabled={false}
                    onChange={this.changeTimeSlot}
                    title={`${msToAmPmHours(selectedTimeSlot.from)}–${msToAmPmHours(selectedTimeSlot.to)}`}
                    data-testid={`${appointmentTypeId}${selectedTimeSlot.from}`}
                  />
                  <Button view="underlined" onClick={this.changeTimeSlot}>
                    Change time slot
                  </Button>
                </>
              ) : (
                <Accordion>
                  {Object.entries(slots).map(([sectionName, sectionSlots]) => (
                    <AccordionItem
                      key={sectionName}
                      header={sectionName}
                      open={
                        (Media.greaterThan(screenWidth, Media.BREAKPOINTS.XS) ||
                          componentRendered) &&
                        sectionName === displayedSectionName
                      }
                      disabled={!sectionSlots.length}
                      onClick={(event) => this.onAccordionChangeHandler(event, sectionName)}
                    >
                      <ul className={styles.TimeSlotsSectionWithAccordion__list}>
                        {sectionSlots.map((slot) => (
                          <li
                            key={`${appointmentTypeId}${slot.from}`}
                            className={classnames({
                              [styles.TimeSlotsSectionWithAccordion__listItem_withTooltip]:
                                slot.disabled,
                            })}
                          >
                            <SelectableTimeSlot
                              propName="slot"
                              slot={slot}
                              isChecked={
                                selectedTimeSlot &&
                                `${appointmentTypeId}${slot.from}` ===
                                  `${selectedTimeSlot.appointmentTypeId}${selectedTimeSlot.from}`
                              }
                              isDisabled={slot.disabled}
                              onChange={this.selectTimeSlot}
                              title={`${msToAmPmHours(slot.from)}–${msToAmPmHours(slot.to)}`}
                              data-testid={`${appointmentTypeId}${slot.from}`}
                            />
                            {slot.disabled && (
                              <Tooltip
                                id={`${appointmentTypeId}_tooltip_${slot.from}`}
                                showContent={false}
                                anchor="center"
                                data-testid="tooltip"
                              >
                                {timeSlotTooltipText}
                              </Tooltip>
                            )}
                          </li>
                        ))}
                      </ul>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </div>
        )}
      </Media>
    );
  }
}

TimeSlotsSectionWithAccordion.propTypes = {
  slots: PropTypes.shape({}),
  appointmentType: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  selectedTimeSlot: PropTypes.shape({
    from: PropTypes.number,
  }),
  displayedSectionName: PropTypes.string,
  isAppointmentBeingEdited: PropTypes.bool,
  selectedDate: PropTypes.string.isRequired,
  selectTimeSlot: PropTypes.func.isRequired,
  timeSlotAccordionTitle: PropTypes.string,
  shouldCollapse: PropTypes.func.isRequired,
  hasSelectedRelatedTimeSlot: PropTypes.bool,
  shouldBeClosed: PropTypes.bool.isRequired,
  timeSlotTooltipText: PropTypes.string,
};

TimeSlotsSectionWithAccordion.defaultProps = {
  slots: {},
  selectedTimeSlot: null,
  displayedSectionName: '',
  timeSlotAccordionTitle: 'Treatment',
  hasSelectedRelatedTimeSlot: false,
  isAppointmentBeingEdited: false,
  timeSlotTooltipText: '',
};
